import type { AssistantContextSnapshot } from '../../assistant/context/assistantContextTypes';
import { WORKFLOW_KEYS, type AssistantAction, type AssistantPageContext, type AssistantSeed } from './types';

// The 'lodgeic:assistant:open' event → what the v2 dock opens.
//
// Every "Ask AI" entry point dispatches the same event v1 reads (see
// AssistantLauncher): a prompt or a native action (prompt, promptKey,
// workflowRef, label), a property (hotelId), a one-shot preamble
// (contextPrefix), and the page publishes what the user is looking at as an
// assistant_context.v1 snapshot. v1 sends all of that with the turn; this
// maps it onto v2's chat request — first message, workflow action, scope
// (locationIds) and page context — so no entry point loses anything when the
// assistant-v2 flag is on. Pure, so it is tested without rendering the dock.

/** detail of the open event, as the entry points send it. */
export interface AssistantOpenDetail {
  /** v1: open full screen. The v2 dock has its own Full page button. */
  expanded?: boolean;
  threadId?: string | null;
  /** A property id, or 'all'. */
  hotelId?: string | number | null;
  hotelName?: string;
  /** Several properties (a portfolio report row). */
  locationIds?: Array<string | number>;
  /** v1: put this in the box. v2 sends it (or drafts it, with focusComposer). */
  prompt?: string;
  promptKey?: string;
  /** A one-shot preamble for the next message (the daily briefing's card). */
  contextPrefix?: string;
  focusComposer?: boolean;
  /** A native action: run on open. */
  action?: {
    prompt?: string;
    promptKey?: string;
    workflowRef?: string;
    workflowContext?: { versionId?: number | null; year?: number | null } | null;
    label?: string;
  } | null;
  /** What the user is looking at, when the entry point knows more than the page snapshot (a report row). */
  context?: AssistantPageContext;
  /** Already shaped for v2 (Forecast V3). */
  v2Seed?: AssistantSeed;
}

export type V2Open =
  /** A new conversation that starts with this. */
  | { kind: 'seed'; seed: AssistantSeed }
  /** An existing conversation. */
  | { kind: 'thread'; threadId: string }
  /** The dock as it is; a conversation not started yet follows the page's property. */
  | { kind: 'resume' };

// The service's limits (assistant/api src/agent/pageContext.ts). Clamped here
// so an oversized page never turns into a refused question.
const KEY = /^[A-Za-z0-9_.-]{1,48}$/;
const ISO = /^\d{4}-\d{2}-\d{2}$/;
const MAX_NOTE = 6000;

function posInt(v: unknown): number | null {
  const n = typeof v === 'number' ? v : typeof v === 'string' && /^\d+$/.test(v.trim()) ? Number(v) : NaN;
  return Number.isInteger(n) && n > 0 ? n : null;
}

function yearOf(v: unknown): number | null {
  const n = posInt(v);
  return n && n >= 2000 && n <= 2100 ? n : null;
}

function clip(v: unknown, max: number): string | undefined {
  if (typeof v !== 'string') return undefined;
  const t = v.trim();
  if (!t) return undefined;
  return t.length > max ? `${t.slice(0, max - 1).trimEnd()}…` : t;
}

function isoDate(v: unknown): string | undefined {
  return typeof v === 'string' && ISO.test(v) ? v : undefined;
}

/** The workflow a v1 workflowRef runs ('budget_review.v1' → 'budget_review'), if v2 has it. */
export function workflowKeyFromRef(ref: string | null | undefined): AssistantAction['workflow'] | null {
  const key = String(ref || '').split('.')[0];
  return (WORKFLOW_KEYS as readonly string[]).includes(key) ? key : null;
}

/** Drops what the service would refuse and anything empty; undefined when nothing is left. */
export function cleanPageContext(c: AssistantPageContext | null | undefined): AssistantPageContext | undefined {
  if (!c) return undefined;
  const out: AssistantPageContext = {};
  if (c.page && KEY.test(c.page)) out.page = c.page;
  const label = clip(c.label, 240);
  if (label) out.label = label;
  const source = clip(c.source, 120);
  if (source) out.source = source;
  if (c.period) {
    const p: NonNullable<AssistantPageContext['period']> = {};
    const start = isoDate(c.period.start);
    const end = isoDate(c.period.end);
    const plabel = clip(c.period.label, 80);
    if (start) p.start = start;
    if (end) p.end = end;
    if (plabel) p.label = plabel;
    if (Number.isInteger(c.period.year) && c.period.year! >= 2000 && c.period.year! <= 2100) p.year = c.period.year;
    if (Number.isInteger(c.period.month) && c.period.month! >= 1 && c.period.month! <= 12) p.month = c.period.month;
    if (Object.keys(p).length) out.period = p;
  }
  if (c.selection && KEY.test(c.selection.kind || '')) {
    const s: NonNullable<AssistantPageContext['selection']> = { kind: c.selection.kind };
    if (Number.isInteger(c.selection.id)) s.id = c.selection.id;
    const date = isoDate(c.selection.date);
    if (date) s.date = date;
    const slabel = clip(c.selection.label, 200);
    if (slabel) s.label = slabel;
    const path = (c.selection.path || []).map((x) => clip(x, 120)).filter((x): x is string => !!x).slice(0, 8);
    if (path.length) s.path = path;
    out.selection = s;
  }
  const versionId = posInt(c.version?.id);
  if (versionId) out.version = { id: versionId, ...(c.version?.kind === 'budget' || c.version?.kind === 'forecast' ? { kind: c.version.kind } : {}) };
  const filters: Record<string, string | number> = {};
  for (const [k, v] of Object.entries(c.filters || {})) {
    if (Object.keys(filters).length >= 12) break;
    if (!KEY.test(k)) continue;
    if (typeof v === 'number' && Number.isFinite(v)) filters[k] = v;
    else if (clip(v, 120)) filters[k] = clip(v, 120)!;
  }
  if (Object.keys(filters).length) out.filters = filters;
  const ids = Array.from(new Set((c.propertyIds || []).map(posInt).filter((n): n is number => n != null))).slice(0, 50);
  if (ids.length) out.propertyIds = ids;
  const note = clip(c.note, MAX_NOTE);
  if (note) out.note = note;
  return Object.keys(out).length ? out : undefined;
}

/** The page the user is on (assistant_context.v1 snapshot) as v2 page context. */
export function pageContextFromSnapshot(snapshot: AssistantContextSnapshot | null | undefined): AssistantPageContext | undefined {
  const e = snapshot?.envelope;
  if (!e) return undefined;
  const v = e.view || {};
  const mode = v.mode === 'budget' || v.mode === 'forecast' ? v.mode : undefined;
  const ref = (e.dataRefs || []).find((r) => r.kind === 'forecast_version' || r.kind === 'budget_version');
  const versionId = v.versionId ?? ref?.id ?? null;
  return cleanPageContext({
    page: e.pageKey,
    label: snapshot?.label,
    propertyIds: e.scope?.propertyIds,
    period: {
      start: v.startDate ?? undefined,
      end: v.endDate ?? undefined,
      year: v.year ?? undefined,
      month: v.activeMonth ?? undefined,
    },
    ...(versionId ? { version: { id: versionId, kind: mode ?? (ref?.kind === 'budget_version' ? 'budget' : ref ? 'forecast' : undefined) } } : {}),
    ...(e.selection
      ? { selection: { kind: e.selection.kind, id: e.selection.id ?? undefined, date: e.selection.date ?? undefined, label: e.selection.label ?? undefined } }
      : {}),
    filters: e.filters,
  });
}

/** The properties a button is about: hotelId ('all' = every property), else its locationIds, else the page's. */
function scopeFrom(d: AssistantOpenDetail, page: AssistantPageContext | undefined): number[] | null | undefined {
  if (d.hotelId === 'all') return null;
  const one = posInt(d.hotelId);
  if (one) return [one];
  const many = Array.from(new Set((d.locationIds || []).map(posInt).filter((n): n is number => n != null)));
  if (many.length) return many;
  return page?.propertyIds?.length ? page.propertyIds : undefined;
}

/** Map an open event onto the v2 dock. `prompts` is the v1 prompt library (promptKey → text). */
export function toV2Open(
  detail: AssistantOpenDetail | null | undefined,
  opts: { snapshot?: AssistantContextSnapshot | null; prompts?: Record<string, string> } = {},
): V2Open {
  const d = detail || {};
  const page = pageContextFromSnapshot(opts.snapshot);
  if (d.v2Seed?.text) {
    // Forecast V3 shapes its own seed; the page adds the version, month and selected row.
    const context = cleanPageContext({ ...page, ...d.v2Seed.context });
    return { kind: 'seed', seed: { ...d.v2Seed, ...(context ? { context } : {}) } };
  }
  if (d.threadId) return { kind: 'thread', threadId: String(d.threadId) };

  const a = d.action || null;
  const promptKey = a?.promptKey || d.promptKey || null;
  const fromLibrary = promptKey && typeof opts.prompts?.[promptKey] === 'string' ? opts.prompts[promptKey] : '';
  const raw = a?.prompt || d.prompt || fromLibrary || '';
  const scope = scopeFrom(d, page);
  const workflow = a?.workflowRef ? workflowKeyFromRef(a.workflowRef) : null;
  // A workflow runs for one property; without one it goes as a question and the model asks.
  const propertyId = scope && scope.length === 1 ? scope[0] : null;
  const action: AssistantAction | null =
    workflow && propertyId
      ? { workflow, propertyId, versionId: posInt(a?.workflowContext?.versionId), year: yearOf(a?.workflowContext?.year) }
      : null;
  const context = cleanPageContext({
    ...page,
    ...d.context,
    // The properties the button is about, when it names them; else the page's.
    propertyIds: d.context?.propertyIds ?? (scope === undefined ? page?.propertyIds : scope ?? undefined),
    ...(d.contextPrefix ? { note: d.contextPrefix } : {}),
    ...(a?.label ? { source: a.label } : {}),
  });
  const base = { ...(scope !== undefined ? { scope } : {}), ...(context ? { context } : {}) };

  // "Ask about this card": v1 put the start of a question in the box for the
  // user to finish. Same here — sending half a sentence would answer nothing.
  if (!a && d.focusComposer) return { kind: 'seed', seed: { text: raw, draft: true, ...base } };
  const text = raw.trim() || (action ? a?.label?.trim() || 'Run this workflow' : '');
  if (text) return { kind: 'seed', seed: { text, ...(action ? { action } : {}), ...base } };
  // A preamble with nothing to send yet: it rides on whatever the user asks first.
  if (context?.note || d.context) return { kind: 'seed', seed: { text: '', draft: true, ...base } };
  return { kind: 'resume' };
}

/** The "asked from" line on a user message. */
export function contextChipLabel(c: AssistantPageContext | null | undefined): string | null {
  if (!c) return null;
  const what = c.label || [c.selection?.label, c.period?.label].filter(Boolean).join(' · ');
  const text = [c.source, what].filter(Boolean).join(' · ');
  return text || null;
}
